import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Mail } from "lucide-react";

import { useLang, useLangPrefix } from "@/contexts/lang";

export const Route = createFileRoute("/about_/nadzoren-odbor")({
	headers: () => ({
		"Cache-Control":
			"public, max-age=0, s-maxage=86400, stale-while-revalidate=604800",
	}),
	head: () => ({
		meta: [
			{ title: "Надзорен одбор | Македонијатурист АД" },
			{
				name: "description",
				content:
					"Членови на Надзорниот одбор на Македонијатурист АД. Надзор врз работењето и стратешки насоки за раст.",
			},
		],
		links: [
			{
				rel: "canonical",
				href: "https://makedonijaturist.com.mk/about/nadzoren-odbor",
			},
		],
	}),
	component: NadzorenOdborPage,
});

export function NadzorenOdborPage() {
	const t = useLang();
	const prefix = useLangPrefix();

	const members = [
		{
			id: "president",
			name: t.nadzorenOdbor.presidentName,
			title: t.nadzorenOdbor.presidentTitle,
		},
		{
			id: "member-1",
			name: t.nadzorenOdbor.memberName,
			title: t.nadzorenOdbor.memberTitle,
		},
		{
			id: "member-2",
			name: t.nadzorenOdbor.memberName,
			title: t.nadzorenOdbor.memberTitle,
		},
	];

	return (
		<main className="bg-background">
			{/* Hero */}
			<section className="mt-grain relative overflow-hidden bg-primary text-white">
				<div className="pointer-events-none absolute inset-0">
					<div className="absolute -top-[20%] right-0 h-[600px] w-[600px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.12),transparent)] blur-[100px]" />
					<div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
				</div>
				<div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16 md:py-28">
					<a
						href={`${prefix}/about`}
						className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-white/60 hover:text-white/90 transition-colors duration-300 mb-6 sm:mb-8"
					>
						<ArrowLeft className="h-3.5 w-3.5" />
						{t.nadzorenOdbor.backLink}
					</a>
					<div className="max-w-3xl">
						<p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">
							{t.nadzorenOdbor.orgLabel}
						</p>
						<h1 className="mt-4 sm:mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08]">
							{t.nadzorenOdbor.heading}
						</h1>
						<p className="mt-4 sm:mt-6 max-w-2xl text-base sm:text-lg text-white/80 font-light leading-relaxed">
							{t.nadzorenOdbor.intro}
						</p>
					</div>
				</div>
			</section>

			{/* Members */}
			<section className="bg-background py-12 sm:py-16 md:py-24">
				<div className="mx-auto max-w-7xl px-4 sm:px-6">
					<div className="mb-10 sm:mb-14">
						<span className="inline-block h-1 w-12 bg-accent mb-4 sm:mb-6" />
						<h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight text-primary leading-[1.1]">
							{t.nadzorenOdbor.membersHeading}
						</h2>
					</div>

					<div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
						{members.map((member) => (
							<div
								key={member.id}
								className="rounded-xl md:rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm"
							>
								<div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/5">
									<span className="font-display text-xl font-medium text-primary">
										{member.name.charAt(0)}
									</span>
								</div>
								<p className="text-xs font-bold tracking-[0.18em] uppercase text-accent mb-2">
									{member.title}
								</p>
								<h3 className="font-display text-lg sm:text-xl font-medium text-primary ">
									{member.name}
								</h3>
							</div>
						))}
					</div>

					{/* Responsibilities */}
					<div className="mt-12 sm:mt-16 rounded-xl md:rounded-2xl border border-border bg-secondary p-6 sm:p-10">
						<h3 className="font-display text-xl sm:text-2xl font-medium text-primary mb-4">
							{t.nadzorenOdbor.responsibilities}
						</h3>
						<p className="text-sm text-foreground/80 font-light leading-relaxed">
							{t.nadzorenOdbor.responsibilitiesText}{" "}
							<a
								href={`${prefix}/about/povazni-akti`}
								className="font-medium text-accent underline underline-offset-2 hover:text-accent/80 transition-colors"
							>
								{t.nadzorenOdbor.moreLink}
							</a>
						</p>
					</div>

					{/* Contact */}
					<div className="mt-10 sm:mt-12 flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-sm text-muted-foreground font-light">
							{t.nadzorenOdbor.contactNote}
						</p>
						<a
							href={`${prefix}/contact`}
							className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary/90"
						>
							<Mail className="h-4 w-4" />
							{t.nadzorenOdbor.contactBtn}
						</a>
					</div>
				</div>
			</section>
		</main>
	);
}
